import React, { useEffect, useState } from "react";
import { Users, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Live Discord server widget — compact strip showing current online member
 * count + the top 8 active members' avatars, with a Join button.
 *
 * Backs onto GET /api/discord/widget which caches 60s. Component polls every
 * 60s to pick up new counts. Renders nothing when not configured — zero
 * layout impact when the admin hasn't set the server ID yet.
 */
export default function DiscordLiveWidget({ fallbackInvite }) {
  const [widget, setWidget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/discord/widget");
        if (!cancelled) setWidget(data);
      } catch {
        /* silent — never block the homepage */
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!widget?.enabled) return null;

  const invite = widget.instant_invite || fallbackInvite || "https://discord.com";
  const online = widget.presence_count || 0;
  const members = (widget.members || []).slice(0, 8);

  return (
    <section
      data-testid="home-discord-live-widget"
      className="max-w-7xl mx-auto px-6 mt-10"
    >
      <div className="relative overflow-hidden rounded-2xl glass-dark p-4 md:p-5">
        <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-red-500/15 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative shrink-0">
              <div className="h-10 w-10 rounded-xl bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M20.317 4.37a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.009c.12.098.246.198.373.293a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </div>
              <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-black animate-pulse" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-mono text-red-400">Live on Discord</div>
              <div className="font-display font-bold text-sm truncate">
                <span className="text-emerald-400">{online}</span>
                <span className="text-zinc-300"> {online === 1 ? "member" : "members"} online</span>
                {widget.name ? <span className="text-zinc-500 hidden sm:inline"> · {widget.name}</span> : null}
              </div>
            </div>
          </div>

          {members.length > 0 && (
            <div className="hidden md:flex items-center gap-1 ml-2">
              {members.map((m, i) => (
                <img
                  key={m.id || i}
                  src={m.avatar_url}
                  alt={m.username || "member"}
                  title={m.username}
                  className="h-7 w-7 rounded-full border border-black object-cover -ml-1.5 first:ml-0"
                  style={{ zIndex: members.length - i }}
                />
              ))}
              {online > members.length && (
                <span className="h-7 min-w-[28px] px-2 rounded-full bg-white/5 border border-black text-[11px] font-mono font-bold text-zinc-300 flex items-center justify-center -ml-1.5">
                  +{Math.max(0, online - members.length)}
                </span>
              )}
            </div>
          )}

          <a
            href={invite}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="discord-live-join"
            className="ml-auto btn-liquid !py-2 !px-4 !text-xs shrink-0"
          >
            <Users size={12} /> Join server <ArrowRight size={12} />
          </a>
        </div>
      </div>
    </section>
  );
}
